import BaseStore from '@/store/baseStore'
import { buildRevertedMapping } from '@/utils/waterLineRuleUtils'
import { get, set } from 'lodash'
import { unsetAndCleanUpEmtpy } from '@/utils/object'
import { getNewId } from '@/utils/id'
import api from '@/api/api'

export const namespaced = true

const baseUrl = '/water_line_rules/'

const baseStore = new BaseStore(baseUrl)

const getDefaultState = () => ({
  ...structuredClone(baseStore.state),
  unavailableMapping: null,
  availableMapping: null,
  loadingMapping: false,
  mappingFields: ['rule_type', 'name', 'line', 'condition'],
  loadingData: false,
  writeLoading: false,
  updatingData: false
})

export const state = getDefaultState()

export const mutations = {
  ...baseStore.mutations,
  SET_AVAILABLE_MAPPING(state, mapping) {
    state.availableMapping = mapping
  },
  TOGGLE_LOADING_MAPPING(state, value) {
    state.loadingMapping = typeof value === 'undefined' ? !state.loadingMapping : value
  },
  TOGGLE_LOADING_DATA(state) {
    state.loadingData = !state.loadingData
  },
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },
  TOGGLE_UPDATING_DATA(state) {
    state.updatingData = !state.updatingData
  }
}

export const actions = {
  ...baseStore.actions,
  async fetchMapping({ commit, state, getters }, projectId) {
    try {
      commit('TOGGLE_LOADING_MAPPING', true)
      const url = `${state.baseUrl}${projectId}/unavailable_choices_mapping/`
      const { data } = await api.get(url)
      const availableMapping = buildRevertedMapping({
        arraysOfChoices: getters.getArraysOfChoices,
        excludedMapping: data
      })
      commit('SET_AVAILABLE_MAPPING', availableMapping)
    } finally {
      commit('TOGGLE_LOADING_MAPPING', false)
    }
  },
  setAvailableMappingByPath({ commit, state }, { path, value = true }) {
    const { availableMapping } = state
    set(availableMapping, path, value)
    commit('SET_AVAILABLE_MAPPING', { ...availableMapping })
  },
  unsetAvailableMappingByPath({ commit, state }, path) {
    const { availableMapping } = state
    unsetAndCleanUpEmtpy(availableMapping, path)
    commit('SET_AVAILABLE_MAPPING', { ...availableMapping })
  },
  updateAvailableMapping({ dispatch, state }, { item, isAddItem }) {
    const path = state.mappingFields.map((prop) => item[prop])
    if (path.every(Boolean)) {
      isAddItem
        ? dispatch('unsetAvailableMappingByPath', path)
        : dispatch('setAvailableMappingByPath', { path })
    }
  },
  addNewRule({ commit, state }, { revisionId }) {
    const newItem = {
      id: getNewId(state.items, 'id'),
      water_line_rule_revision: revisionId,
      line: null,
      condition: null,
      name: null,
      depth: null,
      bulging: null,
      boundary: null,
      pressure_drop: null,
      rule_type: null
    }
    commit('ADD_ITEM', newItem)
    commit('ADD_DIRTY_ITEM', newItem)
  },
  toggleLoadingData({ commit }) {
    commit('TOGGLE_LOADING_DATA')
  },
  resetState({ commit }) {
    commit('RESET_STATE')
  },
  toggleUpdatingData({ commit }) {
    commit('TOGGLE_UPDATING_DATA')
  }
}

export const getters = {
  getArraysOfChoices(state) {
    return state.mappingFields.map((key) =>
      get(state.options, [key, 'choices'], []).map((choice) => choice.value)
    )
  },
  getAvailableValuesByPath:
    (state) =>
    (path = []) => {
      const availableMapping = state.availableMapping || {}
      const obj = path.length > 0 ? get(state.availableMapping, path, {}) : availableMapping
      return Object.keys(obj)
    },
  getChoices:
    (state, getters) =>
    ({ availableValuesPath = [], optionKey }) => {
      const availableValues = getters.getAvailableValuesByPath(availableValuesPath)
      return get(state.options, [optionKey, 'choices'], []).map((choice) => ({
        ...choice,
        disabled: !availableValues.includes(choice.value)
      }))
    },
  getRuleTypeChoices(state, getters) {
    return getters.getChoices({ availableValuesPath: [], optionKey: 'rule_type' })
  },
  getTopoProfilePointNameChoices:
    (state, getters) =>
    ({ ruleType }) => {
      return getters.getChoices({ availableValuesPath: [ruleType], optionKey: 'name' })
    },
  getLineChoices:
    (state, getters) =>
    ({ ruleType, topoProfilePointName }) => {
      return getters.getChoices({
        availableValuesPath: [ruleType, topoProfilePointName],
        optionKey: 'line'
      })
    },
  getConditionChoices:
    (state, getters) =>
    ({ ruleType, topoProfilePointName, line }) => {
      return getters.getChoices({
        availableValuesPath: [ruleType, topoProfilePointName, line],
        optionKey: 'condition'
      })
    }
}
